import { Injectable } from '@angular/core';

/**
 * The Utils Service instance.
 */
@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  /**
   * Get hash substring method.
   *
   * @param key The key identifier.
   * @returns Returns the substring at the specified location within a String object.
   */
  static getHashSubstring(key: number) {
    return window.location.hash.substring(key);
  }

  /**
   * Get hash param method.
   *
   * @param key The key identifier.
   * @param url The url identifier.
   * @returns null
   */
  static getHashParam(key: string, url: string) {
    const hash = url.split('#')[1];
    if (hash) {
      const matches = hash.match(new RegExp(key + '=([^&]*)'));
      return matches ? matches[1] : null;
    } else {
      return null;
    }
  }

  /**
   * Get hash param from url method.
   *
   * @param key The key identifier.
   * @returns
   */
  static getHashParamFromUrl(key: string) {
    const matches = window.location.hash.match(new RegExp(key + '=([^&]*)'));
    return matches ? matches[1] : null;
  }

  /**
   * The allowed language method.
   *
   * @param lang The language identifier.
   * @returns
   */
  static allowedLanguage(lang: string) {
    return lang.toLowerCase() === 'ro' || lang.toLowerCase() === 'en';
  }

  /**
   * Convert Locale method.
   *
   * @param lang The language identifier.
   * @returns
   */
  static convertLocale(lang: string) {
    return lang.toLowerCase() === 'ro' ? 'ro-RO' : 'en-US';
  }

  /**
   * Get query params from url method.
   *
   * @returns
   */
  static getQueryParamsFromUrl() {
    const a = window.location.search.substr(1).split('&');
    if (a[0] === '') {
      return {};
    }
    const b = {};
    for (const item of a) {
      if (item) {
        const p = item.split('=', 2);
        if (p.length === 1) {
          b[p[0]] = '';
        } else {
          b[p[0]] = decodeURIComponent(p[1].replace(/\+/g, ' '));
        }
      }
    }
    return b;
  }

  /**
   * The encode base64 method.
   *
   * @param object The object identifier
   * @returns
   */
  static encodeBase64(object: any) {
    return window.btoa(JSON.stringify(object));
  }

  /**
   * The decode base64 method.
   *
   * @param string The string identifier.
   * @returns
   */
  static decodeBase64(string: string): any {
    const decodedString = window.atob(string);
    return JSON.parse(decodedString);
  }

  /**
   * The remove hash from url method.
   *
   * @param url The url identifier.
   * @returns string
   */
  static removeHashFromUrl(url: string) {
    let returnUrl = url.split('#')[0].replace('/', '');
    returnUrl = returnUrl.split('?')[0];
    return returnUrl;
  }

  /**
   * Get parameter by name method.
   *
   * @param name The name identifier.
   * @returns
   */
  static getParameterByName(name: string) {
    const url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  /**
   * The browser os version method.
   *
   * @returns
   */
  static browserOsVersion() {
    const unknown = '-';

    // browser
    const nVer = navigator.appVersion;
    const nAgt = navigator.userAgent;
    let browser = navigator.appName;
    let version = '' + parseFloat(navigator.appVersion);
    let majorVersion = parseInt(navigator.appVersion, 10);
    let nameOffset;
    let verOffset;
    let ix;

    verOffset = nAgt.indexOf('Opera');

    // Opera
    if (nAgt.indexOf('Opera') !== -1) {
      verOffset = nAgt.indexOf('Opera');
      browser = 'Opera';
      version = nAgt.substring(verOffset + 6);
      if (nAgt.indexOf('Version') !== -1) {
        verOffset = nAgt.indexOf('Version');
        version = nAgt.substring(verOffset + 8);
      }
    }
    // Opera Next
    if (nAgt.indexOf('OPR') !== -1) {
      verOffset = nAgt.indexOf('OPR');
      browser = 'Opera';
      version = nAgt.substring(verOffset + 4);
    } else if (nAgt.indexOf('Edge') !== -1) {
      verOffset = nAgt.indexOf('Edge');
      // Edge
      browser = 'Microsoft Edge';
      version = nAgt.substring(verOffset + 5);
    } else if (nAgt.indexOf('MSIE') !== -1) {
      verOffset = nAgt.indexOf('MSIE');
      // MSIE
      browser = 'Microsoft Internet Explorer';
      version = nAgt.substring(verOffset + 5);
    } else if (nAgt.indexOf('Chrome') !== -1) {
      verOffset = nAgt.indexOf('Chrome');
      // Chrome
      browser = 'Chrome';
      version = nAgt.substring(verOffset + 7);
    } else if (nAgt.indexOf('Safari') !== -1) {
      verOffset = nAgt.indexOf('Safari');
      // Safari
      browser = 'Safari';
      version = nAgt.substring(verOffset + 7);
      if (nAgt.indexOf('Version') !== -1) {
        verOffset = nAgt.indexOf('Version');
        version = nAgt.substring(verOffset + 8);
      }
    } else if (nAgt.indexOf('Firefox') !== -1) {
      verOffset = nAgt.indexOf('Firefox');
      // Firefox
      browser = 'Firefox';
      version = nAgt.substring(verOffset + 8);
    } else if (nAgt.indexOf('Trident/') !== -1) {
      // MSIE 11+
      browser = 'Microsoft Internet Explorer';
      version = nAgt.substring(nAgt.indexOf('rv:') + 3);
    } else if (nAgt.lastIndexOf(' ') + 1 < nAgt.lastIndexOf('/')) {
      nameOffset = nAgt.lastIndexOf(' ') + 1;
      verOffset = nAgt.lastIndexOf('/');
      // Other browsers
      browser = nAgt.substring(nameOffset, verOffset);
      version = nAgt.substring(verOffset + 1);
      if (browser.toLowerCase() === browser.toUpperCase()) {
        browser = navigator.appName;
      }
    }
    // trim the version string
    if (version.indexOf(';') !== -1) {
      ix = version.indexOf(';');
      version = version.substring(0, ix);
    }
    if (version.indexOf(' ') !== -1) {
      ix = version.indexOf(' ');
      version = version.substring(0, ix);
    }
    if (version.indexOf(')') !== -1) {
      ix = version.indexOf(')');
      version = version.substring(0, ix);
    }

    majorVersion = parseInt('' + version, 10);
    if (isNaN(majorVersion)) {
      version = '' + parseFloat(navigator.appVersion);
      majorVersion = parseInt(navigator.appVersion, 10);
    }

    /**
     *  mobile version
     */
    const mobile = /Mobile|mini|Fennec|Android|iP(ad|od|hone)/.test(nVer);

    /**
     * system
     */
    let os = unknown;
    const clientStrings = [
      { s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/ },
      { s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
      { s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/ },
      { s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/ },
      { s: 'Windows Vista', r: /Windows NT 6.0/ },
      { s: 'Windows Server 2003', r: /Windows NT 5.2/ },
      { s: 'Windows XP', r: /(Windows NT 5.1|Windows XP)/ },
      { s: 'Windows 2000', r: /(Windows NT 5.0|Windows 2000)/ },
      { s: 'Windows ME', r: /(Win 9x 4.90|Windows ME)/ },
      { s: 'Windows 98', r: /(Windows 98|Win98)/ },
      { s: 'Windows 95', r: /(Windows 95|Win95|Windows_95)/ },
      { s: 'Windows NT 4.0', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
      { s: 'Windows CE', r: /Windows CE/ },
      { s: 'Windows 3.11', r: /Win16/ },
      { s: 'Android', r: /Android/ },
      { s: 'Open BSD', r: /OpenBSD/ },
      { s: 'Sun OS', r: /SunOS/ },
      { s: 'Linux', r: /(Linux|X11)/ },
      { s: 'iOS', r: /(iPhone|iPad|iPod)/ },
      { s: 'Mac OS X', r: /Mac OS X/ },
      { s: 'Mac OS', r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
      { s: 'QNX', r: /QNX/ },
      { s: 'UNIX', r: /UNIX/ },
      { s: 'BeOS', r: /BeOS/ },
      { s: 'OS/2', r: /OS\/2/ },
      {
        s: 'Search Bot',
        r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/,
      },
    ];

    for (const id in clientStrings) {
      if (clientStrings.hasOwnProperty(id)) {
        const cs = clientStrings[id];
        if (cs.r.test(nAgt)) {
          os = cs.s;
          break;
        }
      }
    }

    let osVersion = unknown;

    if (/Windows/.test(os)) {
      osVersion = /Windows (.*)/.exec(os)[1];
      os = 'Windows';
    }

    switch (os) {
      case 'Mac OS X':
        try {
          osVersion = /Mac OS X (10[\.\_\d]+)/.exec(nAgt)[1];
        } catch (e) {
          osVersion = 'unknown';
        }
        break;

      case 'Android':
        try {
          osVersion = /Android ([\.\_\d]+)/.exec(nAgt)[1];
        } catch (e) {
          osVersion = 'unknown';
        }
        break;
    }

    return {
      browser,
      browserVersion: version,
      browserMajorVersion: majorVersion,
      mobile,
      os,
      osVersion,
    };
  }
}
